import React, { createContext, useContext, useMemo } from 'react';
import type { ViewProps } from 'react-native';

import {
  useCustomerData,
  useForgotPassword,
  useSignIn,
  useSignOut,
  useSignUp,
} from './hooks';
import { useSignUpVerification } from './hooks/useSignUpVerification';

// ────────────────────────────────────────────────────────────────────────────────

const CustomerContext = createContext<CustomerCtx | undefined>(undefined);

export const CustomerProvider = (props: Pick<ViewProps, 'children'>) => {
  const customerDataCtx = useCustomerData();
  const { customer } = customerDataCtx;

  const signUpCtx = useSignUp();
  const forgotPasswordCtx = useForgotPassword();
  const signInCtx = useSignIn();
  const signOutCtx = useSignOut({
    customer,
    resetSignUpData: signUpCtx.resetSignUpData,
  });
  const verifySignUpCtx = useSignUpVerification();

  // ────────────────────────────────────────────────────────────────────

  const value = useMemo(
    () => ({
      ...customerDataCtx,
      ...signInCtx,
      ...forgotPasswordCtx,
      ...signOutCtx,
      ...signUpCtx,
      ...verifySignUpCtx,
    }),
    [
      customerDataCtx,
      forgotPasswordCtx,
      signInCtx,
      signOutCtx,
      signUpCtx,
      verifySignUpCtx,
    ],
  );

  return (
    <CustomerContext.Provider value={value}>
      {props.children}
    </CustomerContext.Provider>
  );
};
// ────────────────────────────────────────────────────────────────────────────────

export const useCustomer = () => {
  const context = useContext(CustomerContext);

  if (context === undefined) {
    throw new Error('useCustomer must be used within a <CustomerProvider>');
  }

  return context;
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type CustomerCtx = ReturnType<typeof useCustomerData> &
  ReturnType<typeof useSignIn> &
  ReturnType<typeof useForgotPassword> &
  ReturnType<typeof useSignOut> &
  ReturnType<typeof useSignUp> &
  ReturnType<typeof useSignUpVerification>;
